/**
 * Tiny client-side validators used by auth pages.
 * Kept dependency-free + ASCII-only to stay test-friendly.
 */

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

export function validateEmail(value) {
  const v = (value || "").trim();
  if (!v) return "Email is required";
  if (!EMAIL_RE.test(v)) return "Enter a valid email address";
  if (v.length > 254) return "Email is too long";
  return null;
}

export function validatePassword(value, { min = 8 } = {}) {
  const v = value || "";
  if (!v) return "Password is required";
  if (v.length < min) return `Password must be at least ${min} characters`;
  return null;
}

export function validateStrongPassword(value) {
  const base = validatePassword(value, { min: 8 });
  if (base) return base;
  const checks = [/[a-z]/, /[A-Z]/, /[0-9]/];
  const failed = checks.filter((re) => !re.test(value)).length;
  if (failed > 1) {
    return "Use a mix of upper, lower case letters and a number";
  }
  return null;
}

/**
 * Map FastAPI / axios error responses to a single human string.
 * Mirrors backend/routes contract: { detail: string | [...] }.
 */
export function pickApiError(err, fallback = "Something went wrong. Please try again.") {
  const data = err?.response?.data;
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ") || fallback;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return err?.message || fallback;
}

/**
 * Friendly mapping of HTTP status → user-facing copy for the login flow.
 * The backend already returns nice strings; this is the safety net for
 * unexpected statuses (network errors, ingress 503s, etc.).
 */
export function loginErrorCopy(err) {
  const status = err?.response?.status;
  const fromDetail = pickApiError(err, "");
  if (fromDetail) return { msg: fromDetail, kind: classifyLoginError(status, fromDetail) };
  if (status === 429) return { msg: "Too many failed attempts. Try again in 15 minutes.", kind: "rate_limited" };
  if (status === 403) return { msg: "Access is restricted for this account.", kind: "forbidden" };
  if (status === 401) return { msg: "Invalid email or password.", kind: "invalid_credentials" };
  if (status >= 500) return { msg: "Our servers are having a moment. Please retry shortly.", kind: "server_down" };
  return { msg: "Unable to sign in. Please check your connection and try again.", kind: "network" };
}

export function classifyLoginError(status, detail) {
  const s = (detail || "").toLowerCase();
  if (status === 429 || s.includes("too many")) return "rate_limited";
  if (status === 403 && s.includes("network is not allowed")) return "ip_blocked";
  if (status === 403 && s.includes("b2b")) return "not_b2b";
  if (status === 403) return "forbidden";
  if (status === 401) return "invalid_credentials";
  if (status >= 500) return "server_down";
  return "unknown";
}
